import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { DebitNoteService } from '../../services/debitNote.service';
import { SupplierBillService } from '../../services/supplierBill.service';
import { DebitNote } from '../../interfaces/procurement/debitNote.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import AllocateDialog, { AllocationTargetOption } from '../allocation/AllocateDialog';
import AllocationsPanel from '../allocation/AllocationsPanel';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';

const DebitNoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SUPPLIER_BILL_MANAGE);
  const canAllocate = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);

  const [note, setNote] = useState<DebitNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [allocRefresh, setAllocRefresh] = useState(0);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);
  const [targets, setTargets] = useState<AllocationTargetOption[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setNote(await DebitNoteService.getById(id));
    } catch (e) {
      toast.showApiError(e, 'Failed to load debit note');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openApply = async () => {
    if (!note) return;
    try {
      const r = await SupplierBillService.listBySupplier(note.supplierId, 0, 500);
      setTargets((r.content ?? [])
        .filter(b => (b.status === 'UNPAID' || b.status === 'PARTIALLY_PAID') && b.id !== note.supplierBillId)
        .map(b => ({
          id: b.id, label: b.billNumber,
          outstanding: b.totalAmount - b.paidAmount - (b.debitedAmount ?? 0),
        }))
        .filter(t => t.outstanding > 0.004));
      setApplyOpen(true);
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier bills');
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!note) return <Box sx={{ p: 3 }}><Typography>Debit note not found.</Typography></Box>;
  const posted = note.status === 'POSTED';

  const doVoid = async () => {
    setBusy(true);
    try {
      await DebitNoteService.voidNote(note.id, voidReason.trim());
      toast.showSuccess('Debit note voided');
      setVoidOpen(false);
      await load();
      setAllocRefresh(k => k + 1);
    } catch (e) {
      toast.showApiError(e, 'Failed to void debit note');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{note.debitNoteNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canAllocate && posted && note.unappliedAmount > 0.004 && (
            <Button data-help="procurement.dnDetail.apply" variant="contained" onClick={openApply}>Apply to bill</Button>
          )}
          <Button data-help="procurement.dnDetail.pdf" variant="outlined" onClick={async () => {
            try { saveBlob(await DebitNoteService.downloadPdf(note.id), `${note.debitNoteNumber}.pdf`); }
            catch (e) { toast.showApiError(e, 'Failed to download PDF'); }
          }}>Mushak 6.8 PDF</Button>
          {canManage && posted && (
            <Button data-help="procurement.dnDetail.void" variant="outlined" color="error" onClick={() => { setVoidReason(''); setVoidOpen(true); }}>Void</Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip size="small" label={humanizeEnum(note.status)} color={posted ? 'success' : 'default'} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Bill</Typography>
            <Link component={RouterLink} to={`/procurement/supplier-bills/${note.supplierBillId}`}>{note.billNumber ?? 'View bill'}</Link>
          </Box>
          <Box><Typography variant="body2" color="text.secondary">Supplier</Typography><Typography>{note.supplierName ?? '—'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Date</Typography><Typography>{formatDate(note.noteDate)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Supplier reference</Typography><Typography>{note.supplierReference ?? '—'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Subtotal</Typography><Typography>{formatMoney(note.subtotal)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Input tax reduced</Typography><Typography>{formatMoney(note.taxAmount)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Total</Typography><Typography fontWeight="bold">{formatMoney(note.totalAmount)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Applied to other bills</Typography><Typography>{formatMoney(note.allocatedAmount)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Unapplied</Typography><Typography fontWeight="bold">{formatMoney(note.unappliedAmount)}</Typography></Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="body2" color="text.secondary">Reason</Typography>
            <Typography>{note.reason}</Typography>
          </Box>
          {note.status === 'VOIDED' && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Voided {note.voidedDate ? formatDate(note.voidedDate) : ''}</Typography>
              <Typography>{note.voidReason}</Typography>
            </Box>
          )}
          {note.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Link component={RouterLink} to={`/accounting/journal-entries/${note.journalEntryId}`}>View JE</Link>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }} data-help-section="procurement.dnDetail.lines">
        <Typography variant="h6" sx={{ mb: 1 }}>Lines</Typography>
        <ScrollableTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="procurement.dnDetail.col.desc">Description</TableCell>
                <TableCell data-help="procurement.dnDetail.col.net" align="right">Net</TableCell>
                <TableCell data-help="procurement.dnDetail.col.tax" align="right">Input tax</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {note.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.description ?? '—'}</TableCell>
                  <TableCell align="right">{formatMoney(l.netAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(l.taxAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      </Paper>

      <AllocationsPanel creditType="PURCHASE_DEBIT_NOTE" creditId={note.id} refreshKey={allocRefresh}
        canReverse={canAllocate && posted} onChanged={load} />

      <AllocateDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onAllocated={() => { load(); setAllocRefresh(k => k + 1); }}
        creditType="PURCHASE_DEBIT_NOTE"
        creditId={note.id}
        available={note.unappliedAmount}
        targetType="SUPPLIER_BILL"
        targets={targets}
        title="Apply debit note to bill"
        targetLabel="Open bill"
      />

      <Dialog open={voidOpen} onClose={() => !busy && setVoidOpen(false)} maxWidth="sm" fullWidth data-help-page="procurement.dnVoidForm.page">
        <DialogTitle>Void debit note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Posts the exact mirror of the debit note and restores the bill's payable and input tax.
          </Typography>
          <TextField data-help="procurement.dnVoidForm.field.reason" label="Reason" required fullWidth multiline minRows={2} value={voidReason}
            onChange={e => setVoidReason(e.target.value)} inputProps={{ maxLength: 1000 }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.dnVoidForm.cancel" onClick={() => setVoidOpen(false)} disabled={busy}>Cancel</Button>
          <Button data-help="procurement.dnDetail.voidConfirm" color="error" variant="contained" disabled={busy || !voidReason.trim()} onClick={doVoid}>Void</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DebitNoteDetail;
